import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Share2, Copy, Check } from 'lucide-react';

// WhatsApp Icon Component
const WhatsAppIcon = ({ size = 20 }) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    fill="currentColor"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Twitter/X Icon Component
const TwitterIcon = ({ size = 20 }) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    fill="currentColor"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Facebook Icon Component  
const FacebookIcon = ({ size = 20 }) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    fill="currentColor"
  >
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

// Telegram Icon Component
const TelegramIcon = ({ size = 20 }) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    fill="currentColor"
  >
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const ShareButtons = ({ 
  prediction, 
  lotteryName,
  variant = 'full', // 'full' | 'compact' | 'icons-only'
  className = ''
}) => {
  const { token } = useAuth();
  const [referralCode, setReferralCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferralCode();
  }, [token]);

  const fetchReferralCode = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/affiliate/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralCode(res.data.referral_code);
    } catch (e) {
      console.log('Error fetching referral code');
    } finally {
      setLoading(false);
    }
  };

  const getShareText = () => {
    const numbers = prediction?.numbers || prediction?.prediction?.numbers || [];
    const numbersStr = numbers.map(n => String(n).padStart(2, '0')).join(' - ');
    const confidence = prediction?.confidence || prediction?.prediction?.confidence || 0;
    
    const baseUrl = window.location.origin;
    const referralLink = referralCode 
      ? `${baseUrl}?ref=${referralCode}` 
      : baseUrl;

    return {
      short: `🎰 Mi predicción para ${lotteryName}: ${numbersStr} (${confidence}% confianza)\n\n🔥 ¡Genera tus números con Sócrates en NumeroMaestro!\n${referralLink}`,
      twitter: `🎰 Predicción Sócrates para ${lotteryName}: ${numbersStr}\n\n🤖 ${confidence}% confianza con #NumeroMaestro\n\n${referralLink}`,
      whatsapp: `🎰 *Mi predicción con Sócrates para ${lotteryName}:*\n\n🔢 Números: *${numbersStr}*\n📊 Confianza: *${confidence}%*\n\n🤖 _Generado con el motor Sócrates_\n\n👉 ¡Obtén tus predicciones aquí!\n${referralLink}`
    };
  };

  const shareToWhatsApp = () => {
    const text = encodeURIComponent(getShareText().whatsapp);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    toast.success('Abriendo WhatsApp...');
  };

  const shareToTwitter = () => {
    const text = encodeURIComponent(getShareText().twitter);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
    toast.success('Abriendo Twitter/X...');
  };

  const shareToFacebook = () => {
    const baseUrl = window.location.origin;
    const referralLink = referralCode 
      ? `${baseUrl}?ref=${referralCode}` 
      : baseUrl;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}&quote=${encodeURIComponent(getShareText().short)}`, '_blank');
    toast.success('Abriendo Facebook...');
  };

  const shareToTelegram = () => {
    const baseUrl = window.location.origin;
    const referralLink = referralCode 
      ? `${baseUrl}?ref=${referralCode}` 
      : baseUrl;
    const text = encodeURIComponent(getShareText().short);
    window.open(`https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${text}`, '_blank');
    toast.success('Abriendo Telegram...');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getShareText().short);
      setCopied(true);
      toast.success('¡Copiado al portapapeles!');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Error al copiar');
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Predicción NumeroMaestro - ${lotteryName}`,
          text: getShareText().short,
          url: referralCode ? `${window.location.origin}?ref=${referralCode}` : window.location.origin
        });
        toast.success('¡Compartido!');
      } catch (e) {
        if (e.name !== 'AbortError') {
          toast.error('Error al compartir');
        }
      }
    } else {
      copyToClipboard();
    }
  };

  if (loading) {
    return null;
  }

  if (variant === 'icons-only') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Button
          size="icon"
          variant="ghost"
          onClick={shareToWhatsApp}
          className="h-9 w-9 text-green-500 hover:text-green-400 hover:bg-green-500/10"
          title="Compartir en WhatsApp"
          data-testid="share-whatsapp-btn"
        >
          <WhatsAppIcon size={18} />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={shareToTwitter}
          className="h-9 w-9 text-slate-400 hover:text-white hover:bg-slate-700"
          title="Compartir en Twitter/X"
          data-testid="share-twitter-btn"
        >
          <TwitterIcon size={18} />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={shareToTelegram}
          className="h-9 w-9 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
          title="Compartir en Telegram"
          data-testid="share-telegram-btn"
        >
          <TelegramIcon size={18} />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={copyToClipboard}
          className="h-9 w-9 text-slate-400 hover:text-white hover:bg-slate-700"
          title="Copiar al portapapeles"
          data-testid="share-copy-btn"
        >
          {copied ? <Check size={18} className="text-green-400" /> : <Copy size={18} />}
        </Button>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Button
          size="sm"
          variant="outline"
          onClick={shareToWhatsApp}
          className="bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20 hover:text-green-300"
          data-testid="share-whatsapp-btn"
        >
          <WhatsAppIcon size={16} />
          <span className="ml-1.5 hidden sm:inline">WhatsApp</span>
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={shareToTwitter}
          className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
          data-testid="share-twitter-btn"
        >
          <TwitterIcon size={16} />
          <span className="ml-1.5 hidden sm:inline">Twitter</span>
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={nativeShare}
          className="bg-purple-500/10 border-purple-500/30 text-purple-400 hover:bg-purple-500/20"
          data-testid="share-more-btn"
        >
          <Share2 size={16} />
        </Button>
      </div>
    );
  }

  // Full variant
  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center gap-2 text-sm text-slate-400">
        <Share2 size={16} />
        <span>Compartir predicción</span>
        {referralCode && (
          <span className="text-purple-400 text-xs">
            (con tu código: {referralCode})
          </span>
        )}
      </div>
      
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={shareToWhatsApp}
          className="bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20 hover:text-green-300"
          data-testid="share-whatsapp-btn"
        >
          <WhatsAppIcon size={18} />
          <span className="ml-2">WhatsApp</span>
        </Button>
        
        <Button
          variant="outline"
          onClick={shareToTwitter}
          className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
          data-testid="share-twitter-btn"
        >
          <TwitterIcon size={18} />
          <span className="ml-2">Twitter/X</span>
        </Button>

        <Button
          variant="outline"
          onClick={shareToFacebook}
          className="bg-blue-600/10 border-blue-600/30 text-blue-400 hover:bg-blue-600/20 hover:text-blue-300"
          data-testid="share-facebook-btn"
        >
          <FacebookIcon size={18} />
          <span className="ml-2">Facebook</span>
        </Button>

        <Button
          variant="outline"
          onClick={shareToTelegram}
          className="bg-sky-500/10 border-sky-500/30 text-sky-400 hover:bg-sky-500/20 hover:text-sky-300"
          data-testid="share-telegram-btn"
        >
          <TelegramIcon size={18} />
          <span className="ml-2">Telegram</span>
        </Button>
        
        <Button
          variant="outline"
          onClick={copyToClipboard}
          className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
          data-testid="share-copy-btn"
        >
          {copied ? (
            <>
              <Check size={18} className="text-green-400" />
              <span className="ml-2">¡Copiado!</span>
            </>
          ) : (
            <>
              <Copy size={18} />
              <span className="ml-2">Copiar</span>
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ShareButtons;
